/**
 * Readability helpers for Message Heatmap (insight copy, peak band, top cells).
 * Does not change volume tiers or band thresholds — only presentation helpers.
 */

import { FILTER_INACTIVE_GREY } from './messageVolumeTierModel'

/** Quieter low / moderate; high & peak stay strong */
export const HEATMAP_BAND_FILL = {
  0: 'transparent',
  1: '#f4f4f5',
  2: '#ede9fe',
  3: '#8b5cf6',
  4: '#4c1d95',
}

export function heatmapSegmentFill(level, pinnedBand) {
  if (level === 0) return 'transparent'
  if (pinnedBand != null && level !== pinnedBand) return FILTER_INACTIVE_GREY
  return HEATMAP_BAND_FILL[level] ?? HEATMAP_BAND_FILL[0]
}

export function heatmapLegendSwatchFill(bandLevel, pinnedBand) {
  if (pinnedBand != null && bandLevel !== pinnedBand) return FILTER_INACTIVE_GREY
  return HEATMAP_BAND_FILL[bandLevel] ?? HEATMAP_BAND_FILL[0]
}

export function formatHourShort(h) {
  const x = Math.max(0, Math.min(23, Math.floor(h)))
  const isPM = x >= 12
  const d = x === 0 ? 12 : x > 12 ? x - 12 : x
  return `${d}${isPM ? 'PM' : 'AM'}`
}

export function formatHourRangeLabel(startH, endH) {
  if (startH === endH) return `around ${formatHourShort(startH)}`
  return `${formatHourShort(startH)}–${formatHourShort(endH)}`
}

/** Sum message volume per clock hour (matrix[hour][col]). */
export function hourTotalsFromMatrix24(matrix) {
  if (!Array.isArray(matrix) || matrix.length !== 24) return Array(24).fill(0)
  const totals = Array(24).fill(0)
  for (let h = 0; h < 24; h += 1) {
    const row = matrix[h]
    if (!Array.isArray(row)) continue
    for (let c = 0; c < row.length; c += 1) {
      totals[h] += Number(row[c]) || 0
    }
  }
  return totals
}

/**
 * Best contiguous window of `windowSize` hours by total volume.
 * @returns {{ startH: number, endH: number, sum: number } | null}
 */
export function bestPeakHourWindow(hourTotals, windowSize = 4) {
  if (!hourTotals.length) return null
  const win = Math.max(1, Math.min(12, Math.floor(windowSize)))
  let bestSum = -1
  let bestStart = 0
  for (let s = 0; s <= 24 - win; s += 1) {
    let sum = 0
    for (let k = 0; k < win; k += 1) sum += hourTotals[s + k] ?? 0
    if (sum > bestSum) {
      bestSum = sum
      bestStart = s
    }
  }
  if (bestSum <= 0) return null
  return { startH: bestStart, endH: bestStart + win - 1, sum: bestSum }
}

/**
 * Whether a highlight band is meaningful (not flat noise).
 */
export function shouldShowPeakBand(hourTotals, window, grandTotal) {
  if (!window || grandTotal <= 0) return false
  const flatAvg = (grandTotal / 24) * (window.endH - window.startH + 1)
  if (flatAvg <= 0) return false
  return window.sum >= flatAvg * 1.12
}

/** CSS for plot overlay: hour 0 at bottom */
export function peakBandStyleFromHours(startH, endH) {
  const span = endH - startH + 1
  return {
    bottom: `${(startH / 24) * 100}%`,
    height: `${(span / 24) * 100}%`,
  }
}

function sum(arr) {
  return arr.reduce((s, v) => s + v, 0)
}

/** Weekly: columns Sun-first; wDays[i] optional for naming */
export function dayPhraseWeekly(matrix24, wDays) {
  if (!Array.isArray(matrix24) || matrix24.length !== 24) return 'across the week'
  const nCol = matrix24[0]?.length ?? 0
  if (nCol === 0) return 'across the week'
  const colSums = Array(nCol).fill(0)
  for (let h = 0; h < 24; h += 1) {
    const row = matrix24[h]
    if (!Array.isArray(row)) continue
    for (let c = 0; c < nCol; c += 1) colSums[c] += Number(row[c]) || 0
  }
  const total = sum(colSums)
  if (total <= 0) return 'across the week'

  let weekend = 0
  let weekday = 0
  for (let c = 0; c < nCol; c += 1) {
    const isWeekend = c === 0 || c === nCol - 1
    if (isWeekend) weekend += colSums[c]
    else weekday += colSums[c]
  }

  if (weekend > weekday * 1.2) return 'on weekends'
  if (weekday > weekend * 1.2) return 'on weekdays'

  const idx = colSums.map((v, i) => ({ i, v })).sort((a, b) => b.v - a.v)
  const top = idx[0]?.i ?? 0
  const top2 = idx[1]?.i

  const mid = [2, 3, 4]
  if (mid.includes(top) && mid.includes(top2)) return 'mid-week'

  const early = [1, 2]
  if (early.includes(top) && early.includes(top2)) return 'early in the week'

  const late = [4, 5]
  if (late.includes(top) && late.includes(top2)) return 'toward week’s end'

  const name = (c) => {
    const d = wDays?.[c]
    if (d && typeof d === 'string') return shortenWeekdayName(d)
    return ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][c] ?? 'that day'
  }
  if (top2 != null && colSums[top2] > total * 0.12) {
    return `on ${name(top)} and ${name(top2)}`
  }
  return `on ${name(top)}`
}

function shortenWeekdayName(d) {
  const s = String(d)
  return s.length <= 3 ? s : s.slice(0, 3)
}

function parseISODate(iso) {
  if (!iso || typeof iso !== 'string') return null
  const [y, m, day] = iso.split('-').map(Number)
  if (!y || !m || !day) return null
  return new Date(y, m - 1, day)
}

/** Daily view: aggregate by weekday across columns */
export function dayPhraseDaily(matrix24, calDates) {
  if (!Array.isArray(calDates) || calDates.length === 0) return 'across the date range shown'
  const byDow = Array(7).fill(0)
  for (let c = 0; c < calDates.length; c += 1) {
    const dt = parseISODate(calDates[c])
    if (!dt) continue
    const dow = dt.getDay()
    for (let h = 0; h < 24; h += 1) {
      byDow[dow] += Number(matrix24[h]?.[c]) || 0
    }
  }
  const total = sum(byDow)
  if (total <= 0) return 'across the date range shown'

  const weekend = byDow[0] + byDow[6]
  const weekday = total - weekend
  if (weekend > weekday * 1.2) return 'on weekends'
  if (weekday > weekend * 1.2) return 'on weekdays'

  const names = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat']
  const ranked = byDow.map((v, i) => ({ i, v })).sort((a, b) => b.v - a.v)
  const a = ranked[0]?.i
  const b = ranked[1]?.i
  if (a != null && b != null && byDow[b] > total * 0.12) {
    return `on ${names[a]} and ${names[b]}`
  }
  if (a != null) return `on ${names[a]}`
  return 'across the date range shown'
}

function formatMonthShort(ym) {
  if (!ym || typeof ym !== 'string') return ym
  const [y, m] = ym.split('-').map(Number)
  if (!y || !m) return ym
  const dt = new Date(y, m - 1, 1)
  return dt.toLocaleDateString('en-US', { month: 'short' })
}

/** Monthly columns: which months carry most volume */
export function dayPhraseMonthly(matrix24, monthDates) {
  if (!Array.isArray(monthDates) || monthDates.length === 0) return 'across the months shown'
  const colSums = monthDates.map((_, c) => {
    let s = 0
    for (let h = 0; h < 24; h += 1) s += Number(matrix24[h]?.[c]) || 0
    return s
  })
  const total = sum(colSums)
  if (total <= 0) return 'across the months shown'
  const ranked = colSums.map((v, i) => ({ i, v })).sort((a, b) => b.v - a.v)
  const i0 = ranked[0]?.i
  const i1 = ranked[1]?.i
  if (i0 != null && i1 != null && colSums[i1] > total * 0.15) {
    return `in ${formatMonthShort(monthDates[i0])} and ${formatMonthShort(monthDates[i1])}`
  }
  if (i0 != null) return `in ${formatMonthShort(monthDates[i0])}`
  return 'across the months shown'
}

export function buildMessageHeatmapInsight(viewMode, matrix24, context) {
  if (!Array.isArray(matrix24) || matrix24.length !== 24) return null
  const grandTotal = matrix24.flat().reduce((s, v) => s + (Number(v) || 0), 0)
  if (grandTotal <= 0) return null

  const hourTotals = hourTotalsFromMatrix24(matrix24)
  const meanHour = grandTotal / 24
  const maxHourTot = Math.max(...hourTotals)
  const hourContrast = meanHour > 0 ? maxHourTot / meanHour : 0

  let dayPart = 'in this view'
  if (viewMode === 'weekly') dayPart = dayPhraseWeekly(matrix24, context?.wDays)
  else if (viewMode === 'daily') dayPart = dayPhraseDaily(matrix24, context?.calDates)
  else dayPart = dayPhraseMonthly(matrix24, context?.monthDates)

  if (hourContrast < 1.35) {
    return `Message volume is relatively even across hours, with modest variation ${dayPart}.`
  }

  const win = bestPeakHourWindow(hourTotals, 4)
  let timePart = 'across the day'
  if (win && shouldShowPeakBand(hourTotals, win, grandTotal)) {
    timePart = formatHourRangeLabel(win.startH, win.endH)
  } else {
    const hi = hourTotals.indexOf(maxHourTot)
    if (maxHourTot > meanHour * 1.15) timePart = `around ${formatHourShort(hi)}`
  }

  return `Message activity peaks around ${timePart}, strongest ${dayPart}.`
}

export function topThreePeakSlotKeys(matrix24) {
  if (!Array.isArray(matrix24) || matrix24.length !== 24) return new Set()
  const cells = []
  for (let h = 0; h < 24; h += 1) {
    const row = matrix24[h]
    if (!Array.isArray(row)) continue
    for (let c = 0; c < row.length; c += 1) {
      const v = Number(row[c]) || 0
      if (v > 0) cells.push({ h, c, v })
    }
  }
  cells.sort((a, b) => b.v - a.v)
  const keys = new Set()
  for (let i = 0; i < Math.min(3, cells.length); i += 1) keys.add(`${cells[i].h},${cells[i].c}`)
  return keys
}

/** X-axis label step: show first, last, and every `step` index */
export function xAxisLabelStep(count, viewMode) {
  if (count <= 0) return 1
  if (viewMode === 'weekly') return 1
  if (count <= 10) return 1
  if (count <= 20) return 2
  if (count <= 35) return 3
  return Math.max(2, Math.ceil(count / 12))
}
